/**
 * Generic SSE (Server-Sent Events) Client
 * Reusable utility for creating SSE connections with proper authentication
 *
 * Uses @microsoft/fetch-event-source for:
 * - Authorization header support (native EventSource doesn't support this)
 * - Better error handling and retry logic
 * - Modern async/await API
 */

import { fetchEventSource } from "@microsoft/fetch-event-source";
import { getAccessToken } from "@/auth/auth-storage";

/**
 * SSE subscription handle for cleanup
 */
export interface SSESubscription {
  abort: () => void;
}

/**
 * Event handler for SSE messages
 * Receives the parsed event data
 */
export type SSEMessageHandler<T = unknown> = (data: T, event: string) => void;

/**
 * Event handler for SSE errors
 */
export type SSEErrorHandler = (error: Error) => void;

/**
 * Event handler for SSE connection open
 */
export type SSEOpenHandler = () => void;

/**
 * Configuration options for SSE connection
 */
export interface SSEOptions<T = unknown> {
  /**
   * API endpoint path (will be prefixed with /api)
   * Example: '/users/123/events'
   */
  endpoint: string;

  /**
   * Query parameters to append to the URL
   * Example: { token: 'abc123', filter: 'active' }
   */
  params?: Record<string, string>;

  /**
   * Custom headers to include (in addition to auth headers)
   * Auth headers (Authorization, X-Token-Type) are added automatically
   */
  headers?: Record<string, string>;

  /**
   * Callback when a message is received
   * @param data - Parsed event data
   * @param event - Event type (e.g., 'message', 'custom_event')
   */
  onMessage: SSEMessageHandler<T>;

  /**
   * Callback when an error occurs
   * Connection will be closed automatically on error
   */
  onError?: SSEErrorHandler;

  /**
   * Callback when connection opens successfully
   */
  onOpen?: SSEOpenHandler;

  /**
   * Event type to listen for (default: 'message')
   * For custom event types like 'token_used', specify here
   */
  eventType?: string;

  /**
   * Whether to automatically parse JSON data (default: true)
   * Set to false if you want to handle raw string data
   */
  parseJSON?: boolean;

  /**
   * Whether to keep connection open when tab is hidden (default: false)
   */
  openWhenHidden?: boolean;

  /**
   * Whether to include server auth headers (default: true)
   * Set to false for public/unauthenticated SSE endpoints
   */
  includeAuth?: boolean;
}

/**
 * Build the full URL for SSE endpoint
 */
function buildSSEUrl(endpoint: string, params?: Record<string, string>): string {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  const apiPath = import.meta.env.VITE_API_PATH;
  const baseURL = import.meta.env.PROD && apiBaseUrl ? `${apiBaseUrl}${apiPath || ''}` : '';
  let url: string = baseURL ? `${baseURL}${endpoint}` : `/api${endpoint}`;

  if (params) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  return url;
}

/**
 * Build headers for SSE request
 */
function buildSSEHeaders(
  includeAuth: boolean,
  customHeaders?: Record<string, string>
): Record<string, string> {
  const headers: Record<string, string> = {
    'Cache-Control': 'no-cache',
    ...customHeaders,
  };

  if (includeAuth) {
    const accessToken = getAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
  }

  return headers;
}

/**
 * Create a Server-Sent Events connection with authentication
 *
 * @template T - Type of the event data
 * @param options - SSE connection options
 * @returns SSESubscription with abort() method for cleanup
 *
 * @example Basic usage
 * ```typescript
 * const subscription = createSSEConnection({
 *   endpoint: '/users/123/events',
 *   params: { token: 'abc123' },
 *   onMessage: (data) => {
 *     console.log('Event received:', data);
 *     subscription.abort(); // Close connection
 *   },
 *   onError: (error) => {
 *     console.error('SSE error:', error);
 *   }
 * });
 *
 * // Cleanup
 * return () => subscription.abort();
 * ```
 *
 * @example Custom event type
 * ```typescript
 * const subscription = createSSEConnection({
 *   endpoint: '/notifications',
 *   eventType: 'notification',
 *   onMessage: (data) => {
 *     console.log('Notification:', data);
 *   }
 * });
 * ```
 *
 * @example Without authentication
 * ```typescript
 * const subscription = createSSEConnection({
 *   endpoint: '/public/status',
 *   includeAuth: false,
 *   onMessage: (data) => {
 *     console.log('Public event:', data);
 *   }
 * });
 * ```
 */
export function createSSEConnection<T = unknown>(
  options: SSEOptions<T>
): SSESubscription {
  const {
    endpoint,
    params,
    headers: customHeaders,
    onMessage,
    onError,
    onOpen,
    eventType = 'message',
    parseJSON = true,
    openWhenHidden = false,
    includeAuth = true,
  } = options;

  // Build URL and headers
  const url = buildSSEUrl(endpoint, params);
  const headers = buildSSEHeaders(includeAuth, customHeaders);

  // Create AbortController for cleanup
  const abortController = new AbortController();

  // Start SSE connection
  fetchEventSource(url, {
    method: 'GET',
    headers,
    signal: abortController.signal,

    // Called when connection opens
    async onopen(response) {
      if (response.ok) {
        onOpen?.();
        return; // Connection successful
      }

      // Handle errors
      if (response.status >= 400 && response.status < 500 && response.status !== 429) {
        // Client error (4xx) - don't retry
        const errorText = await response.text();
        throw new Error(`SSE connection failed: ${response.status} ${errorText}`);
      } else {
        // Server error (5xx) or rate limit - will retry
        throw new Error(`SSE connection error: ${response.status}`);
      }
    },

    // Called when a message is received
    onmessage(event) {
      try {
        // Check if this is the event type we're listening for
        if (event.event === eventType && event.data) {
          // Parse data if requested
          const data = parseJSON ? JSON.parse(event.data) : event.data;
          onMessage(data, event.event);
        }
      } catch (error) {
        onError?.(
          error instanceof Error ? error : new Error('Failed to parse SSE message')
        );
      }
    },

    // Called on error
    onerror(error) {
      onError?.(
        error instanceof Error ? error : new Error('SSE connection error')
      );
      // Throw to stop automatic reconnection
      throw error;
    },

    // Control visibility behavior
    openWhenHidden,
  });

  // Return subscription object with abort method
  return {
    abort: () => abortController.abort(),
  };
}
